// src/utils/categoryIcons.js

/**
 * Mapeamento abrangente de categorias e variações para seus respectivos ícones PNG personalizados.
 * Todos os ícones seguem o estilo visual de adesivo com borda branca e sombra suave.
 */
export const CATEGORY_ICON_MAP = Object.freeze({
  // 1. Turístico / Turismo / Ecoturismo
  'turistico': '/turistico.png',
  'turisticos': '/turistico.png',
  'turismo': '/turistico.png',
  'ecoturismo': '/turistico.png',
  'passeio': '/turistico.png',
  'atrativo': '/turistico.png',
  'mirante': '/turistico.png',
  'cachoeira': '/turistico.png',

  // 2. Lazer / Recreação / Balneários
  'lazer': '/lazer.png',
  'recreacao': '/lazer.png',
  'balneario': '/lazer.png',
  'parque': '/lazer.png',
  'banho': '/lazer.png',
  'natureza': '/lazer.png',

  // 3. Histórico / Patrimônio / Museus / Memoriais
  'historico': '/historico.png',
  'historicos': '/historico.png',
  'historia': '/historico.png',
  'patrimonio': '/historico.png',
  'memorial': '/historico.png',
  'museu': '/historico.png',
  'estacao': '/historico.png',

  // 4. Religioso / Tradições Religiosas / Igrejas / Santuários
  'religioso': '/religiosos.png',
  'religiosos': '/religiosos.png',
  'religiao': '/religiosos.png',
  'fe': '/religiosos.png',
  'igreja': '/religiosos.png',
  'santuario': '/religiosos.png',
  'capela': '/religiosos.png',
  'romaria': '/religiosos.png',

  // 5. Cultural / Festivais / Festejos / Eventos Culturais
  'cultural': '/eventos-culturais.png',
  'culturais': '/eventos-culturais.png',
  'cultura': '/eventos-culturais.png',
  'evento': '/eventos-culturais.png',
  'eventos': '/eventos-culturais.png',
  'festival': '/eventos-culturais.png',
  'festejo': '/eventos-culturais.png',
  'festa': '/eventos-culturais.png',

  // 6. Gastronomia / Culinária
  'gastronomia': '/Gastronomia.png',
  'gastronomico': '/Gastronomia.png',
  'gastronomicos': '/Gastronomia.png',
  'culinaria': '/Gastronomia.png',
  'comida': '/Gastronomia.png',
  'restaurante': '/Gastronomia.png',

  // 7. Artesanato
  'artesanato': '/artesanato.png',
  'artesao': '/artesanato.png',
  'manual': '/artesanato.png',

  // 8. Artes Visuais / Galerias / Pinacotecas
  'artes': '/artes-visuais.png',
  'artes-visuais': '/artes-visuais.png',
  'artes visuais': '/artes-visuais.png',
  'artesvisuais': '/artes-visuais.png',
  'pintura': '/artes-visuais.png',
  'galeria': '/artes-visuais.png',
  'pinacoteca': '/artes-visuais.png',

  // 9. Esporte / Atividades Esportivas
  'esporte': '/esporte.png',
  'esportes': '/esporte.png',
  'esportivo': '/esporte.png',
  'esportivos': '/esporte.png',
  'futebol': '/esporte.png',
  'corrida': '/esporte.png',

  // 10. Vaquejada / Tradição Nordestina / Cavalgada
  'vaquejada': '/vaquejada.png',
  'vaquejadas': '/vaquejada.png',
  'cavalgada': '/vaquejada.png',
  'rodeio': '/vaquejada.png',

  // 11. Música / Shows / Concertos
  'musica': '/musica.png',
  'musicas': '/musica.png',
  'musical': '/musica.png',
  'show': '/musica.png',
  'shows': '/musica.png',
  'concerto': '/musica.png',

  // 12. Cinema / Audiovisual
  'cinema': '/cinema.png',
  'filme': '/cinema.png',
  'filmes': '/cinema.png',
  'audiovisual': '/cinema.png',

  // 13. Teatro / Artes Cênicas
  'teatro': '/teatro.png',
  'artes cenicas': '/teatro.png',
  'peca': '/teatro.png',

  // 14. Outros / Padrão
  'outros': '/outro.png',
  'outro': '/outro.png',
  'padrao': '/outro.png'
});

/**
 * Cores temáticas para cada categoria no sistema
 */
export const CATEGORY_COLORS = Object.freeze({
  turistico: '#06b6d4',      // Cyan vibrante
  lazer: '#f59e0b',          // Âmbar solar
  historico: '#b45309',      // Terracota / Bronze histórico
  religiosos: '#eab308',     // Amarelo ouro
  religioso: '#eab308',
  culturais: '#ec4899',      // Pink / Magenta festivo
  cultural: '#ec4899',
  gastronomia: '#f97316',    // Laranja gastronomia
  artesanato: '#ea580c',     // Terracota cerâmica
  artes: '#10b981',          // Verde esmeralda
  'artes-visuais': '#10b981',
  esporte: '#14b8a6',        // Verde azulado esportivo
  esportivos: '#14b8a6',
  vaquejada: '#92400e',      // Marrom couro nordestino
  vaquejadas: '#92400e',
  musica: '#3b82f6',         // Azul música
  cinema: '#0ea5e9',         // Ciano cinema
  teatro: '#ef4444',         // Vermelho teatro
  outros: '#94a3b8'          // Slate
});

/**
 * Normaliza um texto para comparação sem acentos, em minúsculas e sem espaços extras.
 */
export const normalizeCategory = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

/**
 * Obtém a URL do ícone correto para um evento, analisando categoria única, array de categorias
 * ou strings compostas separadas por vírgula.
 * 
 * @param {Object} event - Objeto do evento contendo `categoria` e/ou `categorias`
 * @returns {string} Caminho do ícone PNG
 */
export const getEventIconUrl = (event) => {
  if (!event) return '/outro.png';

  const candidates = [];
  if (event.categoria) candidates.push(event.categoria);
  if (Array.isArray(event.categorias)) {
    candidates.push(...event.categorias);
  } else if (typeof event.categorias === 'string') {
    candidates.push(event.categorias);
  }

  for (const rawCat of candidates) {
    if (!rawCat) continue;
    // Suporta categorias compostas ex: "Turistico, Lazer" ou "Histórico/Cultural"
    const tokens = String(rawCat).replace(/\//g, ',').split(',');
    for (const token of tokens) {
      const norm = normalizeCategory(token);
      if (norm && CATEGORY_ICON_MAP[norm]) {
        return CATEGORY_ICON_MAP[norm];
      }
    }
  }

  return '/outro.png';
};

/**
 * Obtém a cor temática de um evento baseado em suas categorias.
 */
export const getEventColor = (event) => {
  if (event?.color) return event.color;
  if (!event) return '#06b6d4';

  const candidates = [];
  if (event.categoria) candidates.push(event.categoria);
  if (Array.isArray(event.categorias)) {
    candidates.push(...event.categorias);
  } else if (typeof event.categorias === 'string') {
    candidates.push(event.categorias);
  }

  for (const rawCat of candidates) {
    if (!rawCat) continue;
    const tokens = String(rawCat).replace(/\//g, ',').split(',');
    for (const token of tokens) {
      const norm = normalizeCategory(token);
      if (norm && CATEGORY_COLORS[norm]) {
        return CATEGORY_COLORS[norm];
      }
    }
  }

  return '#06b6d4';
};

/**
 * Lista de categorias padrão para filtros e cadastros administrativos
 */
export const DEFAULT_CATEGORIES = Object.freeze([
  { id: 'turistico', nome: 'Turístico' },
  { id: 'lazer', nome: 'Lazer' },
  { id: 'historico', nome: 'Histórico' },
  { id: 'culturais', nome: 'Culturais' },
  { id: 'religiosos', nome: 'Religiosos' },
  { id: 'gastronomia', nome: 'Gastronomia' },
  { id: 'artesanato', nome: 'Artesanato' },
  { id: 'artes', nome: 'Artes Visuais' },
  { id: 'esporte', nome: 'Esporte' },
  { id: 'vaquejada', nome: 'Vaquejada' },
  { id: 'musica', nome: 'Música' },
  { id: 'cinema', nome: 'Cinema' },
  { id: 'teatro', nome: 'Teatro' },
  { id: 'outros', nome: 'Outros' }
]);
